import { useGet } from "@/lib/api";
import { fmt, dirColor, relDate, countdown } from "@/lib/format";
import { RecordOutcomeDialog } from "@/components/RecordOutcomeDialog";
import { WeekCalendar } from "@/components/WeekCalendar";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { useAuth } from "@/lib/auth";
import { CalendarDays } from "lucide-react";

export function CalendarPage({ refreshKey, onDataChanged }) {
  const { data, loading } = useGet("/calendar", refreshKey);
  const { isPro } = useAuth();
  if (loading || !data) return <div className="text-center text-[#6B7280] py-20 font-mono-num">Loading calendar…</div>;

  return (
    <div className="fade-up">
      <h1 className="font-display text-2xl font-bold mb-1">Economic Calendar</h1>
      <p className="text-xs text-[#6B7280] mb-4 font-mono-num">This week's US events · model forecasts recorded before release · actuals auto-graded from FRED</p>

      <WeekCalendar refreshKey={refreshKey} />
      {!isPro && (
        <div className="-mt-5 mb-6 text-xs text-[#9CA3AF]">
          <UpgradeDialog trigger={<button data-testid="week-upgrade-link" className="text-[#F59E0B] hover:underline">Unlock consensus & previous for high-impact events with Pro →</button>} />
        </div>
      )}

      <h2 className="font-display text-lg font-semibold mb-1">Model Forecasts · Upcoming Releases</h2>
      <p className="text-xs text-[#6B7280] mb-3 font-mono-num">Graded automatically when FRED publishes the figure · or record manually</p>

      <div data-testid="economic-calendar-table" className="flex flex-col gap-3">
        {data.calendar.map((row) => {
          const due = row.days_until <= 0;
          return (
            <div key={`${row.code}-${row.release_date}`} className="glass rounded-2xl p-4 flex items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-3">
                <div className="shrink-0 w-11 h-11 rounded-xl grid place-items-center"
                  style={{ background: due ? "rgba(245,158,11,0.15)" : "rgba(255,255,255,0.04)" }}>
                  <CalendarDays size={18} className={due ? "text-[#F59E0B]" : "text-[#6B7280]"} />
                </div>
                <div className="min-w-0">
                  <div className="font-display font-semibold text-sm truncate">{row.name}</div>
                  <div className="text-[10px] text-[#6B7280] font-mono-num">
                    {relDate(row.release_date)} · {countdown(row.days_until)} · prev {fmt(row.previous, row.unit)}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 shrink-0">
                <div className="text-right">
                  <div className="text-[10px] text-[#6B7280] font-mono-num uppercase">Forecast</div>
                  <div className={`font-mono-num text-sm font-bold ${dirColor(row.forecast_direction)}`}>
                    {row.forecast_direction} {fmt(row.forecast, row.unit)}
                  </div>
                </div>
                {due && row.prediction_id && (
                  <RecordOutcomeDialog
                    predictionId={row.prediction_id}
                    indicatorName={row.name}
                    unit={row.unit}
                    variant="compact"
                    onRecorded={onDataChanged}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
